import { Spacing } from '@persona-id/design-tokens';
import { ShareIcon } from '@persona-id/design-tokens/icons';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import MultiFileUpload from 'dashboard/components/DocumentTesterPanel/MultiFileUpload';
import TestResults from 'dashboard/components/DocumentTesterPanel/TestResults';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import Button, { IconButton } from 'design-system/components/Button';
import Stack from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';

// TODO: create proper model for this once we have the data
export interface ClassificationTestResult {
  document: string;
  classification: string;
  status: string;
}

// TODO: remove this once we have the backend data
const sampleClassifications: ClassificationTestResult[] = [
  {
    document: 'Document 1 super long name blah blah blah',
    classification: 'Bank Statement',
    status: 'Accepted',
  },
  { document: 'Document 2', classification: 'Vehicle Registration Document', status: 'Rejected' },
];

const PANEL_FOOTER_HEIGHT = 64;

const Form = styled.form`
  flex-direction: column;
  position: relative;
  width: 400px;
  height: 100%;
  overflow: hidden;
`;

const TitleContainer = styled.div`
  padding: ${Spacing[4]} ${Spacing[5]};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BodyContainer = styled.div`
  height: calc(100% - ${PANEL_FOOTER_HEIGHT}px);
  margin-bottom: ${PANEL_FOOTER_HEIGHT}px;
  overflow-y: auto;
`;

const FooterContainer = styled.footer`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 0px;
  height: ${PANEL_FOOTER_HEIGHT}px;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[5]};
`;

const Seperator = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.color.border.primary};
`;

interface DocumentTesterPanelProps {
  onClose: () => void;
}

enum DocumentTesterPanelStep {
  UploadDocuments = 'uploadDocuments',
  ViewTestResults = 'viewTestResults',
}

const DocumentTesterPanel = ({ onClose }: DocumentTesterPanelProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<FileWithPreview[]>([]);
  const [step, setStep] = useState<DocumentTesterPanelStep>(
    DocumentTesterPanelStep.UploadDocuments,
  );

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [onClose]);

  const handleChange = async (newFiles: FileWithPreview[]) => {
    try {
      setUploadedFiles(newFiles);
    } catch (error) {
      console.error('Error storing files:', error);
    }
  };

  const handleRemoveAllUploads = () => {
    setUploadedFiles([]);
  };

  // TODO: make each step into a separate file
  const UploadDocumentsStep = () => (
    <>
      <BodyContainer>
        <TitleContainer>
          <Paragraph size='lg' weight='bold' color='primary'>
            Upload sample documents
          </Paragraph>
          <IconButton id='document-tester-panel__upload-documents-close-button' onClick={onClose}>
            <XIcon />
          </IconButton>
        </TitleContainer>
        <MultiFileUpload files={uploadedFiles} onChange={handleChange} />
      </BodyContainer>
      <FooterContainer>
        <Stack direction='horizontal' style={{ flexGrow: 1 }}>
          <Button
            id='document-tester-panel__save-button'
            width='auto'
            onClick={() => setStep(DocumentTesterPanelStep.ViewTestResults)}
          >
            Save
          </Button>
          <Button
            id='document-tester-panel__cancel-button'
            width='auto'
            variant='basic'
            onClick={onClose}
          >
            Cancel
          </Button>
          {uploadedFiles.length > 0 && (
            <Button
              id='document-tester-panel__remove-all-uploads-button'
              width='auto'
              variant='basic'
              onClick={handleRemoveAllUploads}
            >
              Remove All Uploads
            </Button>
          )}
        </Stack>
      </FooterContainer>
    </>
  );

  const TestResultsStep = () => (
    <>
      <BodyContainer>
        <TitleContainer>
          <Paragraph size='lg' weight='bold' color='primary'>
            Configuration test results
          </Paragraph>
          <IconButton id='document-tester-panel__test-results-close-button' onClick={onClose}>
            <XIcon />
          </IconButton>
        </TitleContainer>
        <Seperator />
        <TestResults classifications={sampleClassifications} />
        <Seperator />
      </BodyContainer>
      <FooterContainer>
        <Stack direction='horizontal' style={{ flexGrow: 1 }}>
          <Button
            id='document-tester-panel__rerun-button'
            width='auto'
            onClick={() => setStep(DocumentTesterPanelStep.ViewTestResults)}
          >
            Rerun
          </Button>
          <Button
            id='document-tester-panel__upload-more-documents-button'
            width='auto'
            variant='basic'
            prefixIcon={<ShareIcon />}
            onClick={() => setStep(DocumentTesterPanelStep.UploadDocuments)}
          >
            Upload Documents
          </Button>
        </Stack>
        <Paragraph size='sm' color='secondary'>
          {/* TODO: Add correct last ran time */}
          Last ran 1 minute ago
        </Paragraph>
      </FooterContainer>
    </>
  );

  return (
    <Form id='document-tester-panel__form'>
      {step === DocumentTesterPanelStep.UploadDocuments && <UploadDocumentsStep />}
      {step === DocumentTesterPanelStep.ViewTestResults && <TestResultsStep />}
    </Form>
  );
};

export default DocumentTesterPanel;
